import {
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FC, useContext, useEffect, useState } from 'react';
import { BsTrash } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import AlertDialogComponent from '../../../../components/Generic/AlertModal';
import { CoupomAPI } from '../../api';
import {
  CoupomContextValue,
  CoupomFormContext,
  CoupomFormContextDefaultValue,
} from '../../context/CreateCoupomFormContext';
import { CreateListContext } from '../../context/ListContext';
import { Coupom } from '../../models/coupom';
import { VtexCoupom } from '../../models/vtex-coupom';
import CoupomForm from '../CoupomForm';

type CreateCoupomModalProps = {
  isOpen: boolean;
  onClose: () => void;
  data?: Coupom;
};

const EditCoupomModal: FC<CreateCoupomModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const toast = useToast();
  const [formContext, setFormContext] = useState<CoupomContextValue>(
    CoupomFormContextDefaultValue,
  );
  const { updateListFn, paginationInfo } = useContext(CreateListContext)[0];
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const {
    isOpen: isOpenDelete,
    onClose: onCloseDelete,
    onOpen: onOpenDelete,
  } = useDisclosure();
  const { t } = useTranslation();
  useEffect(() => {
    if (!data) return;
    setIsModalLoading(true);
    const coupomAPI = new CoupomAPI();
    coupomAPI
      .findOne(data.id)
      .then(coupom => {
        if (coupom.sync && coupom.coupom) {
          setFormContext({
            vtexCoupom: new VtexCoupom({
              ...coupom.coupom,
              beginDate: coupom.coupom.beginDateUtc,
              endDate: coupom.coupom.endDateUtc,
              status: coupom.coupom.isActive ? 'active' : 'inactive',
            }),
            hasVtexCoupom: true,
            activeCoupom: coupom.customization.active_coupon,
            countdown: coupom.customization.active_countdown,
            rules: coupom.customization.rules,
            description: coupom.customization.description,
            queryVtex: coupom.customization.vtex_query,
          });
        }
      })
      .catch(err => {
        setIsError(true);
        toast({
          title: 'Opss!!',
          description: `${t('couponFetchError')}: ${
            err.response?.data.message || ''
          }`,
          status: 'error',
          position: 'top-right',
        });
      })
      .finally(() => setIsModalLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const resetContext = () => {
    setFormContext(CoupomFormContextDefaultValue);
  };

  const handleCloseModal = () => {
    onClose();
    const timeout = setTimeout(() => {
      clearTimeout(timeout);
      resetContext();
      setIsError(false);
      updateListFn(paginationInfo.page, paginationInfo.limit);
    }, 500);
  };

  const handleEdit = async () => {
    setIsLoading(true);
    const coupomAPI = new CoupomAPI();
    await coupomAPI
      .update(data?.id as string, {
        active_coupon: formContext.activeCoupom,
        active_countdown: formContext.countdown,
        rules: formContext.rules
          .filter(rule => rule !== '')
          .map(rule => rule.trim()),
        description: formContext.description?.trim(),
        vtex_query: formContext.queryVtex?.trim(),
      })
      .then(() => {
        toast({
          title: t('success'),
          description: t('couponUpdatedSuccessfully'),
          status: 'success',
          position: 'top-right',
        });
        handleCloseModal();
      })
      .catch(err =>
        toast({
          title: 'Opss!!',
          description: `${t('couponUpdateError')}: ${
            err.response?.data.message || ''
          }`,
          status: 'error',
          position: 'top-right',
        }),
      )
      .finally(() => setIsLoading(false));
  };

  const handleRemove = async () => {
    setIsLoading(true);
    const coupomAPI = new CoupomAPI();
    await coupomAPI
      .delete(data?.id as string)
      .then(() => {
        toast({
          title: t('success'),
          description: t('couponDeletedSuccessfully'),
          status: 'success',
          position: 'top-right',
        });
        handleCloseModal();
      })
      .catch(err =>
        toast({
          title: 'Opss!!',
          description: `${t('couponDeletionError')}: ${
            err.response?.data.message || ''
          }`,
          status: 'error',
          position: 'top-right',
        }),
      )
      .finally(() => {
        setIsLoading(false);
        onCloseDelete();
      });
  };

  return (
    <>
      <AlertDialogComponent
        onClose={onCloseDelete}
        isOpen={isOpenDelete}
        message="Deseja realmente apagar este cupom?"
        headerTitle="Apagar cupom"
        onConfirm={handleRemove}
        isCentered
      />
      <CoupomFormContext.Provider value={[formContext, setFormContext]}>
        <Modal
          isOpen={isOpen}
          onClose={handleCloseModal}
          closeOnOverlayClick={false}
          closeOnEsc={false}
          size="xl"
        >
          <ModalOverlay />
          <ModalContent pb="6">
            <ModalHeader>
              {`t('editCoupon') `}
              {isModalLoading ? (
                <Spinner
                  thickness="2px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="sm"
                />
              ) : undefined}
            </ModalHeader>
            <ModalCloseButton />
            {isError && (
              <ModalBody>
                <Text textAlign="center">{t('couponFetchError')}</Text>
              </ModalBody>
            )}
            {!isModalLoading && !formContext.hasVtexCoupom && !isError && (
              <ModalBody>
                <Text textAlign="center">{t('couponNoLongerExists')}</Text>
              </ModalBody>
            )}
            {formContext.hasVtexCoupom && (
              <ModalBody>
                <CoupomForm />
              </ModalBody>
            )}
            <ModalFooter w="full">
              <IconButton
                icon={<BsTrash />}
                aria-label="Excluir tag"
                mr="auto"
                colorScheme="red"
                onClick={onOpenDelete}
              />
              {formContext.hasVtexCoupom && (
                <>
                  <Button
                    colorScheme="blue"
                    variant="outline"
                    mr={3}
                    onClick={handleCloseModal}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    rightIcon={
                      isLoading ? (
                        <Spinner
                          thickness="2px"
                          speed="0.65s"
                          emptyColor="gray.200"
                          color="blue.500"
                          size="sm"
                        />
                      ) : undefined
                    }
                    onClick={handleEdit}
                    variant="solid"
                    colorScheme="blue"
                  >
                    {t('save')}
                  </Button>
                </>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      </CoupomFormContext.Provider>
    </>
  );
};

export default EditCoupomModal;
